import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Globe, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import logoFull from "../assets/logo-full.png";
import { LANGUAGES, SUPPORTED_LANG_CODES } from "../i18n";
import { useLocalePath } from "../lib/useLocalePath";

const NAV = [
    { to: "/", key: "home" },
    { to: "/about", key: "about" },
    { to: "/products", key: "products" },
    { to: "/process", key: "process" },
    { to: "/export", key: "export" },
    { to: "/gallery", key: "gallery" },
    { to: "/contact", key: "contact" },
];

const LanguageSwitcher = ({ onPick }) => {
    const { i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const onClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    const switchTo = (code) => {
        const segments = location.pathname.split("/").filter(Boolean);
        if (segments.length && SUPPORTED_LANG_CODES.includes(segments[0])) {
            segments.shift();
        }
        const rest = segments.join("/");
        const next =
            code === "en" ? `/${rest}` : `/${code}${rest ? `/${rest}` : ""}`;

        localStorage.setItem("lang", code);
        i18n.changeLanguage(code);
        navigate(next + location.search, { replace: false });
        setOpen(false);
        onPick?.();
    };

    const current = LANGUAGES.find((l) => l.code === i18n.language);

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                data-testid="lang-switcher"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={open}
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#E8E3D9] hover:text-[#C5A059] transition-colors"
            >
                <Globe size={14} strokeWidth={1.5} />
                <span>{current?.code.toUpperCase() || "EN"}</span>
            </button>

            {open && (
                <ul
                    role="listbox"
                    data-testid="lang-menu"
                    className="absolute right-0 mt-3 min-w-[11rem] bg-[#1A1A1A] border border-[#2C2C2C] py-2 z-50 shadow-2xl"
                >
                    {LANGUAGES.map((l) => (
                        <li key={l.code}>
                            <button
                                type="button"
                                role="option"
                                aria-selected={i18n.language === l.code}
                                data-testid={`lang-option-${l.code}`}
                                onClick={() => switchTo(l.code)}
                                className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm text-left transition-colors ${
                                    i18n.language === l.code
                                        ? "text-[#C5A059]"
                                        : "text-[#E8E3D9] hover:text-[#C5A059]"
                                }`}
                            >
                                {l.label}
                                {i18n.language === l.code && <Check size={14} />}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();
    const { t } = useTranslation();
    const lp = useLocalePath();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [location.pathname]);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-[#0D0D0D]/85 backdrop-blur-xl border-b border-[#2C2C2C]"
                    : "bg-transparent"
            }`}
        >
            <div className="px-6 md:px-12 lg:px-20 py-5 flex items-center justify-between gap-6">
                <Link
                    to={lp("/")}
                    data-testid="nav-logo"
                    className="flex items-center group shrink-0"
                >
                    <img
                        src={logoFull}
                        alt="Jagruthi Stones LLP"
                        className="h-14 w-auto shrink-0"
                    />
                </Link>

                <nav className="hidden xl:flex items-center gap-7">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={lp(item.to)}
                            end={item.to === "/"}
                            data-testid={`nav-link-${item.key}`}
                            className={({ isActive }) =>
                                `text-[11px] uppercase tracking-[0.25em] whitespace-nowrap transition-colors ${
                                    isActive
                                        ? "text-[#C5A059]"
                                        : "text-[#E8E3D9] hover:text-[#C5A059]"
                                }`
                            }
                        >
                            {t(`nav.${item.key}`)}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden xl:flex items-center gap-6 shrink-0">
                    <LanguageSwitcher />
                    <Link
                        to={lp("/contact")}
                        data-testid="nav-cta-quote"
                        className="inline-flex items-center whitespace-nowrap bg-[#C5A059] text-[#0D0D0D] px-5 py-3 text-[11px] uppercase tracking-[0.25em] font-medium hover:bg-[#E8E3D9] transition-colors"
                    >
                        {t("nav.cta_quote")}
                    </Link>
                </div>

                <div className="flex items-center gap-4 xl:hidden">
                    <LanguageSwitcher />
                    <button
                        type="button"
                        data-testid="nav-menu-toggle"
                        aria-label={t("nav.menu_toggle")}
                        onClick={() => setOpen((v) => !v)}
                        className="inline-flex items-center justify-center h-10 w-10 border border-[#2C2C2C] text-[#E8E3D9]"
                    >
                        {open ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="xl:hidden border-t border-[#2C2C2C] bg-[#0D0D0D]/95 backdrop-blur-xl"
                >
                    <div className="px-6 py-6 flex flex-col gap-5">
                        {NAV.map((item) => (
                            <NavLink
                                key={item.to}
                                to={lp(item.to)}
                                end={item.to === "/"}
                                data-testid={`mobile-nav-${item.key}`}
                                className={({ isActive }) =>
                                    `text-sm uppercase tracking-[0.25em] ${
                                        isActive
                                            ? "text-[#C5A059]"
                                            : "text-[#E8E3D9]"
                                    }`
                                }
                            >
                                {t(`nav.${item.key}`)}
                            </NavLink>
                        ))}
                        <Link
                            to={lp("/contact")}
                            data-testid="mobile-cta-quote"
                            className="mt-2 bg-[#C5A059] text-[#0D0D0D] px-5 py-3 text-[11px] uppercase tracking-[0.25em] font-medium inline-block text-center"
                        >
                            {t("nav.cta_quote")}
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
};
